import { type ReactNode, type ComponentType } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context";
import styled from "@emotion/styled";
import { RouteChangeTracker } from "../services/analytics/RouteChangeTracker";
import { BottomNavigation } from "../components/organisms/BottomNavigation";

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${(props) => props.theme.colors.tertiary[1000]};
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${(props) => props.theme.colors.neutral.greyLight[300]};
  border-top-color: ${(props) => props.theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

interface PrivateLayoutProps {
  /**
   * Context Provider opcional para envolver las rutas hijas
   * Útil para inyectar contextos específicos de features
   */
  ContextProvider?: ComponentType<{ children: ReactNode }>;
  /**
   * URL a la que redirigir si no está autenticado
   * @default "/"
   */
  redirectTo?: string;
}

/**
 * Layout para rutas privadas que requieren autenticación.
 * 
 * Características:
 * - Verifica autenticación con AWS Amplify v6
 * - Muestra loading mientras verifica la sesión
 * - Redirige a login si no hay sesión activa
 * - Soporta inyección de Context Providers adicionales
 * 
 * @example
 * // Sin provider adicional
 * <Route element={<PrivateLayout />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 * 
 * @example
 * // Con provider adicional
 * <Route element={<PrivateLayout ContextProvider={OfertaProvider} />}>
 *   <Route path="/ofertas" element={<Ofertas />} />
 * </Route>
 */
export const PrivateLayout = ({
  ContextProvider,
  redirectTo = "/",
}: PrivateLayoutProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <>
        <RouteChangeTracker />
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  const content = (
    <>
      <RouteChangeTracker />
      <Outlet />
      <BottomNavigation />
    </>
  );

  if (ContextProvider) {
    return <ContextProvider>{content}</ContextProvider>;
  }

  return content;
};

export default PrivateLayout;

